import {
    type FC
} from "react";
import {
    Text as NativeText,
    View
} from "react-native";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import {
    useCodeViewerContext
} from "../../context";
import type {
    ICodeViewerTitleContentProps,
    ICodeViewerTitleProps
} from "../../type";
import {
    CircleAlert as CircleAlertIcon,
    File as FileIcon
} from "lucide-react-native";
import stylesheet, {
    getCodeTitleStyles
} from "../../stylesheet";
import Icon from "../../../icon";
import Text from "../../../text";

const CodeViewerFileIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <Icon
    customColor={customColor}
    icon={FileIcon}
    color={color}
    size={size}
/>;

const CodeViewerAlertIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <Icon
    customColor={customColor}
    icon={CircleAlertIcon}
    color={color}
    size={size}
/>;

export const CodeViewerTitleContent: FC<ICodeViewerTitleContentProps> = ({
    icon: IconProp,
    context,
    style,
    title,
    ...props
}) => {
    const {
        shapeConfigs,
        typography,
        iconSizes,
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(context.customTheme);

    const isDanger = context.type === "danger";

    const {
        languageChip: languageChipDynamicStyle,
        container: containerDynamicStyle,
        title: titleDynamicStyle
    } = getCodeTitleStyles({
        size: context.size,
        type: context.type,
        shapeConfigs,
        typography,
        radiuses,
        colors,
        spaces
    });

    const renderIcon = () => {
        if(!title && !isDanger) {
            return null;
        }

        const CurrentIcon = IconProp ?? (isDanger ? CodeViewerAlertIcon : CodeViewerFileIcon);

        return <View
            style={stylesheet.titleIcon}
            aria-hidden={true}
        >
            <CurrentIcon
                customColor={isDanger ? colors.content.icon.danger : colors.content.icon.default}
                color={isDanger ? "danger" : "default"}
                size={iconSizes.small}
            />
        </View>;
    };

    const renderTitle = () => {
        if(!title) {
            return null;
        }

        return <NativeText
            style={[
                stylesheet.titleText,
                titleDynamicStyle
            ]}
            numberOfLines={1}
        >
            {title}
        </NativeText>;
    };

    const renderLanguage = () => {
        if(!context.languageLabel) {
            return null;
        }

        return <View
            style={[
                stylesheet.languageChip,
                languageChipDynamicStyle
            ]}
        >
            <Text
                customColor={colors.system.onContainer.neutral}
                customTheme={context.customTheme}
                style={stylesheet.language}
                variant="labelSmallSize"
                numberOfLines={1}
            >
                {context.languageLabel}
            </Text>
        </View>;
    };

    return <View
        {...props}
        style={[
            style,
            stylesheet.title,
            containerDynamicStyle
        ]}
    >
        {renderIcon()}
        {renderTitle()}
        {renderLanguage()}
    </View>;
};

const CodeViewerTitle: FC<ICodeViewerTitleProps> = (props) => {
    const context = useCodeViewerContext({
        part: "title"
    });

    if(!context) {
        return null;
    }

    return <CodeViewerTitleContent
        {...props}
        context={context}
    />;
};
export default Object.assign(CodeViewerTitle, {
    codeViewerPart: "title" as const
});
